import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type TextareaHTMLAttributes,
} from "react";
import { useMentionableOrgUsersQuery } from "../../api/queries/orgUsersQueries";
import {
  buildMentionToken,
  getActiveMentionQuery,
  type MentionableOrgUser,
} from "../../utils/customerNoteMentions";

type MentionTextareaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "value" | "onChange"
> & {
  value: string;
  onChange: (value: string) => void;
};

export function MentionTextarea({
  value,
  onChange,
  className = "",
  onKeyDown,
  ...props
}: MentionTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const { data: users = [] } = useMentionableOrgUsersQuery();

  const activeMention = useMemo(
    () => getActiveMentionQuery(value, cursorPosition),
    [value, cursorPosition],
  );

  const filteredUsers = useMemo(() => {
    if (!activeMention) return [];
    const query = activeMention.query.toLowerCase();
    return users
      .filter((user) => {
        if (!query) return true;
        return (
          user.username.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query)
        );
      })
      .slice(0, 8);
  }, [activeMention, users]);

  useEffect(() => {
    setDropdownOpen(Boolean(activeMention && filteredUsers.length > 0));
    setHighlightedIndex(0);
  }, [activeMention, filteredUsers.length]);

  const updateCursor = useCallback(() => {
    const position = textareaRef.current?.selectionStart ?? 0;
    setCursorPosition(position);
  }, []);

  const insertMention = useCallback(
    (user: MentionableOrgUser) => {
      if (!activeMention) return;
      const before = value.slice(0, activeMention.start);
      const after = value.slice(cursorPosition);
      const token = buildMentionToken(user);
      const spacer = after.startsWith(" ") || after.length === 0 ? "" : " ";
      const nextValue = `${before}${token}${spacer}${after}`;
      onChange(nextValue);
      setDropdownOpen(false);

      requestAnimationFrame(() => {
        const nextCursor = before.length + token.length + spacer.length;
        textareaRef.current?.focus();
        textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
        setCursorPosition(nextCursor);
      });
    },
    [activeMention, cursorPosition, onChange, value],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (dropdownOpen && filteredUsers.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((index) => (index + 1) % filteredUsers.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex(
          (index) => (index - 1 + filteredUsers.length) % filteredUsers.length,
        );
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        insertMention(filteredUsers[highlightedIndex]);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setDropdownOpen(false);
        return;
      }
    }

    onKeyDown?.(event);
  };

  return (
    <div className="relative">
      <textarea
        {...props}
        ref={textareaRef}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setCursorPosition(event.target.selectionStart ?? 0);
        }}
        onClick={updateCursor}
        onKeyUp={updateCursor}
        onSelect={updateCursor}
        onKeyDown={handleKeyDown}
        className={className}
      />

      {dropdownOpen ? (
        <ul className="absolute bottom-full left-0 right-0 z-50 mb-1 max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {filteredUsers.map((user, index) => (
            <li key={user.id}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => insertMention(user)}
                className={`flex w-full flex-col px-3 py-2 text-left text-sm ${
                  index === highlightedIndex
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span className="font-medium">{user.username}</span>
                <span className="text-xs text-slate-500">{user.email}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
